import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox, InputNumber, Select, Space, Table, Tag, message } from 'antd';
import practiceClient from '../../client/PracticeClient';
import useTranslation from '../../hooks/useTranslation';
import { formatRelativeDay } from '../../utils/relativeDays';
import './DeckItemsTable.css';

const PAGE_SIZE = 25;

/** Порядок по умолчанию — по тому, о чём методика спрашивает первым. */
const DEFAULT_SORT = {
    FIXED_SCHEDULE: 'DUE',
    ADAPTIVE_SCHEDULE: 'DUE',
    MASTERY_RULE: 'CORRECT',
};

/**
 * Элементы колоды с их состоянием памяти: порядок по любому столбцу, фильтры, страницы.
 *
 * Столбцы зависят от методики, и это главное здесь: у лестницы человеку нужна коробка,
 * у адаптивной — стабильность и срок, у критерия — сколько верных набрано. Показывать
 * все поля разом значило бы показать поля, которых выбранная политика не использует, —
 * то есть предложить объяснение, которого не было. Данные те же самые: состояние памяти
 * одно, меняется только то, о чём его спросили.
 *
 * Сортировка и фильтры — на сервере, по индексам: таблица шлёт столбец, направление и
 * условия, а не перекладывает страницу в памяти. Иначе «по сроку» упорядочило бы
 * двадцать пять строк из двадцати тысяч.
 */
const DeckItemsTable = ({ deckId, practiceType, learningMethod, boxCount }) => {
    const { t, currentLanguage } = useTranslation();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    // Порядок помнит, под какую методику он выбран: смена методики меняет и столбцы, и
    // порядок по умолчанию, и выбранный человеком столбец в новой таблице может
    // отсутствовать. Считается прямо в рендере, а не эффектом: эффект успевал бы
    // отправить один запрос со старым столбцом и тут же второй — с новым.
    const [chosenSort, setChosenSort] = useState(null);
    const sort = chosenSort && chosenSort.method === learningMethod
        ? chosenSort
        : { method: learningMethod, column: DEFAULT_SORT[learningMethod] ?? 'DUE', direction: 'ASC' };
    const [filters, setFilters] = useState({
        leech: false, overdue: false, notStarted: false, box: null, rangeStart: null, rangeEnd: null,
    });
    // Диапазон набирается в поле и уходит в запрос по Enter или уходу из поля, а не на
    // каждую клавишу: у сервера каждый такой запрос — проход по колоде до нужного
    // номера, и «123» стоило бы трёх таких проходов.
    const [rangeDraft, setRangeDraft] = useState({ rangeStart: null, rangeEnd: null });
    // Раскрытые строки квиза: подпись — это текст вопроса, и целиком он показывается
    // только по клику, иначе любая сортировка бесполезна на таком экране.
    const [expanded, setExpanded] = useState(() => new Set());

    // Один счётчик на все запросы таблицы: и на первую страницу после смены входов, и
    // на страницы по клику. Ответ применяется, только если он на последний запрос:
    // ответ на третью страницу прежнего порядка не должен перезаписать первую нового.
    const latestRequest = useRef(0);

    const load = useCallback(async (nextPage) => {
        latestRequest.current += 1;
        const requestId = latestRequest.current;
        const isLatest = () => requestId === latestRequest.current;
        setIsLoading(true);
        try {
            const response = await practiceClient.getDeckLearningItems(deckId, practiceType, {
                sort: sort.column,
                direction: sort.direction,
                page: nextPage,
                size: PAGE_SIZE,
                leech: filters.leech,
                overdue: filters.overdue,
                notStarted: filters.notStarted,
                box: filters.box,
                rangeStart: filters.rangeStart,
                rangeEnd: filters.rangeEnd,
            });
            if (isLatest()) {
                setItems(response.items ?? []);
                setTotal(response.total ?? 0);
                setPage(response.page ?? nextPage);
            }
        } catch (error) {
            if (isLatest()) {
                console.error('Не удалось загрузить элементы колоды:', error);
                message.error(t('practice.method.itemsFailed'));
            }
        } finally {
            if (isLatest()) {
                setIsLoading(false);
            }
        }
    }, [deckId, practiceType, sort.column, sort.direction, filters, t]);

    // Любая смена входов читает список заново с первой страницы: остаться на пятой
    // странице прежнего порядка значит показать строки, которые под новым туда не
    // попадают. Методика стоит в зависимостях сама: между лестницей и адаптивными
    // интервалами порядок один, но сервер при переключении перепланирует сроки.
    useEffect(() => {
        load(0);
        const requests = latestRequest;
        return () => {
            requests.current += 1;
        };
    }, [load, learningMethod]);

    const setFilter = (patch) => setFilters((current) => ({ ...current, ...patch }));

    const commitRange = () => {
        if (rangeDraft.rangeStart !== filters.rangeStart || rangeDraft.rangeEnd !== filters.rangeEnd) {
            setFilter(rangeDraft);
        }
    };

    const relative = (iso) => formatRelativeDay(iso, currentLanguage) ?? '—';
    const stateOnly = (render) => (value, item) => (item.started ? render(value, item) : '—');

    const toggleExpanded = (itemId) => setExpanded((current) => {
        const next = new Set(current);
        if (next.has(itemId)) {
            next.delete(itemId);
        } else {
            next.add(itemId);
        }
        return next;
    });

    const labelOf = (label, item) => {
        if (!label) {
            return <i className="deckItems__gone">{t('practice.method.itemGone')}</i>;
        }
        if (practiceType !== 'QUIZ') {
            return <span>{label}</span>;
        }
        const isExpanded = expanded.has(item.itemId);
        return (
            <button
                type="button"
                className={`deckItems__question${isExpanded ? '' : ' deckItems__question--clamped'}`}
                aria-expanded={isExpanded}
                title={isExpanded ? t('progress.collapse') : t('progress.expand')}
                onClick={() => toggleExpanded(item.itemId)}
            >
                {label}
            </button>
        );
    };

    const labelColumn = {
        title: t('practice.method.columnItem'),
        dataIndex: 'label',
        key: 'label',
        render: (label, item) => (
            <span className="deckItems__label">
                {labelOf(label, item)}
                {item.leech && <Tag color="volcano">{t('practice.method.leech')}</Tag>}
                {!item.started && <Tag>{t('progress.notStartedRow')}</Tag>}
            </span>
        ),
    };

    const orderOf = (key) => {
        if (sort.column !== key) {
            return null;
        }
        return sort.direction === 'ASC' ? 'ascend' : 'descend';
    };
    const sortable = (column, key) => ({
        ...column,
        key,
        sorter: true,
        sortOrder: orderOf(key),
    });

    const correctColumn = sortable({
        title: t('practice.method.columnCorrect'),
        align: 'right',
        render: stateOnly((_, item) => `${item.correctCount} / ${item.correctCount + item.incorrectCount}`),
    }, 'CORRECT');
    const dueColumn = sortable({
        title: t('practice.method.columnDue'),
        dataIndex: 'dueAt',
        render: stateOnly(relative),
    }, 'DUE');
    const lastAnsweredColumn = sortable({
        title: t('practice.method.columnLastAnswered'),
        dataIndex: 'lastAnsweredAt',
        render: stateOnly(relative),
    }, 'LAST_ANSWERED');
    const streakColumn = sortable({
        title: t('practice.method.columnStreak'),
        dataIndex: 'streak',
        align: 'right',
        render: stateOnly((streak) => streak),
    }, 'STREAK');

    const columnsByMethod = {
        FIXED_SCHEDULE: [
            labelColumn,
            sortable({
                title: t('practice.method.columnBox'),
                dataIndex: 'box',
                align: 'right',
                render: stateOnly((box) => box),
            }, 'LEVEL'),
            dueColumn,
            correctColumn,
            lastAnsweredColumn,
        ],
        ADAPTIVE_SCHEDULE: [
            labelColumn,
            dueColumn,
            sortable({
                title: t('practice.method.columnStability'),
                dataIndex: 'stability',
                align: 'right',
                // Пусто — след памяти ещё не посчитан: элемент вёлся лестницей и
                // переедет на модель с первым же ответом. Придумывать здесь число
                // значило бы показать стабильность, которой ни один ответ не дал.
                render: stateOnly((stability) => (stability == null ? '—' : `${stability.toFixed(1)} ${t('practice.method.days')}`)),
            }, 'STABILITY'),
            correctColumn,
            lastAnsweredColumn,
        ],
        MASTERY_RULE: [
            labelColumn,
            correctColumn,
            streakColumn,
            lastAnsweredColumn,
        ],
    };

    // antd отдаёт сюда действующую сортировку при любом изменении, в том числе при
    // клике по странице, — поэтому смотрим на причину вызова, а не на наличие
    // сортировки. Третий клик по столбцу у antd «без сортировки»: столбца в sorter
    // уже нет, и он считается кликом по текущему столбцу с возвратом к возрастанию —
    // без сортировки список не бывает.
    const onTableChange = (pagination, _filters, sorter, extra) => {
        if (extra?.action === 'sort') {
            const column = sorter?.columnKey ?? sort.column;
            const direction = sorter?.order === 'descend' ? 'DESC' : 'ASC';
            if (column !== sort.column || direction !== sort.direction) {
                setChosenSort({ method: learningMethod, column, direction });
            }
            return;
        }
        if (pagination.current - 1 !== page) {
            load(pagination.current - 1);
        }
    };

    const boxOptions = Array.from({ length: (boxCount ?? 0) + 1 }, (_, box) => ({
        value: box,
        label: t('practice.box', { number: box + 1 }),
    }));

    return (
        <div className="deckItems">
            <Space className="deckItems__filters" wrap size="middle">
                <Checkbox
                    checked={filters.notStarted}
                    onChange={(event) => setFilter({ notStarted: event.target.checked })}
                >
                    {t('progress.filterNotStarted')}
                </Checkbox>
                {/* У нетронутых нет состояния: остальные условия к ним неприменимы. */}
                <Checkbox
                    checked={filters.leech}
                    disabled={filters.notStarted}
                    onChange={(event) => setFilter({ leech: event.target.checked })}
                >
                    {t('progress.filterLeech')}
                </Checkbox>
                {learningMethod !== 'MASTERY_RULE' && (
                    <Checkbox
                        checked={filters.overdue}
                        disabled={filters.notStarted}
                        onChange={(event) => setFilter({ overdue: event.target.checked })}
                    >
                        {t('progress.filterOverdue')}
                    </Checkbox>
                )}
                {learningMethod === 'FIXED_SCHEDULE' && boxOptions.length > 0 && (
                    <Select
                        className="deckItems__box"
                        allowClear
                        placeholder={t('progress.filterBox')}
                        aria-label={t('progress.filterBox')}
                        disabled={filters.notStarted}
                        value={filters.box}
                        options={boxOptions}
                        onChange={(box) => setFilter({ box: box ?? null })}
                    />
                )}
                <Space size="small" className="deckItems__range">
                    <span>{t('progress.filterRange')}</span>
                    <InputNumber
                        min={1}
                        placeholder={t('progress.from')}
                        aria-label={t('progress.from')}
                        disabled={filters.notStarted}
                        value={rangeDraft.rangeStart}
                        onChange={(value) => setRangeDraft((draft) => ({ ...draft, rangeStart: value ?? null }))}
                        onBlur={commitRange}
                        onPressEnter={commitRange}
                    />
                    <span>–</span>
                    <InputNumber
                        min={1}
                        placeholder={t('progress.to')}
                        aria-label={t('progress.to')}
                        disabled={filters.notStarted}
                        value={rangeDraft.rangeEnd}
                        onChange={(value) => setRangeDraft((draft) => ({ ...draft, rangeEnd: value ?? null }))}
                        onBlur={commitRange}
                        onPressEnter={commitRange}
                    />
                </Space>
            </Space>

            <Table
                className="deckItems__table"
                size="small"
                rowKey="itemId"
                loading={isLoading}
                columns={columnsByMethod[learningMethod] ?? columnsByMethod.FIXED_SCHEDULE}
                dataSource={items}
                onChange={onTableChange}
                pagination={{
                    current: page + 1,
                    pageSize: PAGE_SIZE,
                    total,
                    showSizeChanger: false,
                }}
            />
        </div>
    );
};

export default DeckItemsTable;
